import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Calculator, 
  BarChart3, 
  FileText, 
  Users, 
  Webhook,
  Upload,
  ArrowRight,
  Check,
  Shield,
  Building2,
  Hammer,
  Layers,
  Target,
  Lock,
  Briefcase,
  HardHat,
  Home,
  X
} from "lucide-react";

export default function LandingPage({ user, scrollToPricing }) {
  const pricingRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (scrollToPricing && pricingRef.current) {
      pricingRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scrollToPricing]);

  // Hammer & Anvil Logo Component
  const ForgeIcon = ({ className = "w-8 h-8" }) => (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      {/* Anvil base */}
      <path d="M6 28H34V32C34 33.1 33.1 34 32 34H8C6.9 34 6 33.1 6 32V28Z" fill="currentColor"/>
      <path d="M4 24H36V28H4V24Z" fill="currentColor"/>
      <path d="M10 20H30V24H10V20Z" fill="currentColor"/>
      {/* Hammer */}
      <path d="M20 6L24 10L20 14L16 10L20 6Z" fill="#D4AF37"/>
      <path d="M18 12L22 16V20H18V16L18 12Z" fill="#D4AF37"/>
      {/* Sparks */}
      <circle cx="12" cy="18" r="1" fill="#D4AF37"/>
      <circle cx="28" cy="16" r="1" fill="#D4AF37"/>
      <circle cx="14" cy="14" r="0.5" fill="#D4AF37"/>
    </svg>
  );

  const capabilities = [
    {
      icon: Layers,
      title: "END-TO-END LIFECYCLE",
      description: "Lead to close to post-close. Every stage of the deal in one system. No more juggling between apps."
    },
    {
      icon: Calculator,
      title: "CREATIVE FINANCE ENGINE",
      description: "Subject-To, Seller Finance, Wraps—accurate amortization and balloon math built in. Run 6 strategies instantly."
    },
    {
      icon: HardHat,
      title: "CONTRACTOR MARKETPLACE",
      description: "Verified, insured contractors submit real bids. Estimates flow directly into your underwriting calculations."
    },
    {
      icon: Building2,
      title: "LENDER NETWORK",
      description: "Hard money, private lenders, and bridge loan providers compete for your deals. Funding lined up before you close."
    },
    {
      icon: Target,
      title: "DECISION SUPPORT",
      description: "Confidence scores, risk flags, and next-action prompts. AI-powered analysis tells you what you're missing."
    },
    {
      icon: Webhook,
      title: "FUNNEL INTEGRATION",
      description: "Inbound webhooks auto-create deals from your marketing. Built-in dialer for calls and SMS follow-ups."
    },
    {
      icon: Lock,
      title: "SINGLE SOURCE OF TRUTH",
      description: "Deal files, photos, contracts, notes—everything locked to the deal, searchable and never scattered."
    },
    {
      icon: Users,
      title: "BUYER & REALTOR MATCHING",
      description: "Match deals to your cash buyers instantly. Premium realtor network for retail exits when you're ready to list."
    }
  ];

  const replacesTools = [
    "Spreadsheets & Excel",
    "Google Docs",
    "Podio / Monday",
    "REI BlackBook",
    "Deal Machine",
    "Canva for packets",
    "Contractor hunting",
    "Scattered notes"
  ];

  const plans = [
    {
      name: "STARTER",
      price: "$99.99",
      period: "/mo",
      description: "For operators building their system",
      features: [
        "Up to 25 active deals",
        "Single scenario calculator",
        "Deal Command Center",
        "Deal file locker",
        "Photos & owner info",
        "Notes & activity log",
        "CSV/Excel import",
        "Inbound lead webhook"
      ],
      excluded: [
        "Multi-scenario analysis",
        "Creative finance calculators",
        "Contractor marketplace access",
        "PDF deal packets",
        "Buyer matching"
      ],
      cta: "Start 14-Day Trial",
      highlighted: false
    },
    {
      name: "PRO",
      price: "$149.99",
      period: "/mo",
      description: "For serious operators closing deals",
      badge: "MOST POPULAR",
      features: [
        "Unlimited deals",
        "Multi-scenario calculator",
        "Creative finance (Sub-To, SF, Wrap)",
        "AI-powered deal scoring",
        "PDF deal packets",
        "Buyer list & matching",
        "Deal comparison",
        "Contractor marketplace access",
        "Priority support"
      ],
      excluded: [],
      cta: "Start 14-Day Trial",
      highlighted: true
    },
    {
      name: "OPERATOR",
      price: "$299.99",
      period: "/mo",
      description: "For teams running volume",
      features: [
        "Everything in Pro",
        "Team collaboration",
        "Role-based permissions",
        "White-label PDFs",
        "Custom integrations",
        "API access",
        "Dedicated support",
        "Transaction coordination tools"
      ],
      excluded: [],
      cta: "Start 14-Day Trial",
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo.png" alt="DealForge" className="h-24 w-auto" />
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#capabilities" className="text-white/70 hover:text-white transition-all duration-300 text-sm font-medium">Capabilities</a>
              <a href="#pricing" className="text-white/70 hover:text-white transition-all duration-300 text-sm font-medium">Pricing</a>
              <Link to="/contractor/register" className="text-white/70 hover:text-gold transition-all duration-300 text-sm font-medium">For Contractors</Link>
              <Link to="/lender/login" className="text-white/70 hover:text-gold transition-all duration-300 text-sm font-medium">For Lenders</Link>
            </div>
            
            <div className="flex items-center gap-4">
              {user ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wide rounded-none px-6"
                  data-testid="go-to-dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-[#0A0A0A]/80 backdrop-blur-sm" data-testid="login-btn">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-gold text-black hover:bg-gold-light font-bold uppercase tracking-wide rounded-none px-6" data-testid="get-started-btn">
                      Get Access
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-black" />
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08] text-white/70 text-sm font-mono mb-8">
              <Building2 className="w-4 h-4 text-gold" />
              THE OPERATING SYSTEM FOR REAL ESTATE OPERATORS
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[1.1] mb-8">
              <span className="text-white">STOP MANAGING DEALS.</span>
              <br />
              <span className="text-gold">START CLOSING THEM.</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
              DealForge is infrastructure for real estate execution. Analysis, pipeline, contractors, and close—unified. 
              Replace the tools. Eliminate the friction. Own the outcome.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/register">
                <Button className="gold-gradient-btn h-14 px-10 text-base rounded-none group" data-testid="hero-get-started-btn">
                  REQUEST EARLY ACCESS
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="#capabilities">
                <Button variant="outline" className="h-14 px-10 text-base rounded-none border-white/[0.08] text-white hover:border-white/[0.15] hover:bg-[#0A0A0A]/80 backdrop-blur-sm/50">
                  SEE CAPABILITIES
                </Button>
              </a>
            </div>
            
            <p className="text-xs text-white/70 uppercase tracking-wider">
              Founders pricing available for early adopters. Limited seats.
            </p>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 bg-[#0A0A0A]/80 backdrop-blur-sm/30 border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase text-white mb-6">
              THE SYSTEM IS BROKEN
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10">
              Deals die in spreadsheets. Contractors ghost. Rehab estimates are fiction. 
              You're stitching together 8 tools that don't talk to each other, re-entering data, 
              and losing deals to disorganization—not competition.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {replacesTools.map((tool, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-white/70">
                  <X className="w-4 h-4 text-red-500/60" />
                  <span className="line-through">{tool}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section id="capabilities" className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase text-white mb-4">
              INFRASTRUCTURE, NOT SOFTWARE
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              DealForge isn't another tool. It's the foundation your operation runs on. 
              Every capability designed to reduce friction between you and closed deals.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0A0A0A]/80 backdrop-blur-sm">
            {capabilities.map((cap, idx) => (
              <div 
                key={idx} 
                className="bg-black p-8 group hover:bg-[#0A0A0A]/80 backdrop-blur-sm/50 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] flex items-center justify-center mb-5 group-hover:border-gold/50 transition-all duration-300">
                  <cap.icon className="w-6 h-6 text-gold" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-3">{cap.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{cap.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contractor Marketplace Highlight */}
      <section className="py-24 bg-[#0A0A0A]/80 backdrop-blur-sm/30 border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/30 text-gold text-xs font-bold uppercase tracking-wider mb-6">
                <HardHat className="w-4 h-4" />
                NEW: CONTRACTOR MARKETPLACE
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase text-white mb-6">
                REAL BIDS FROM <span className="text-gold">VERIFIED CONTRACTORS</span>
              </h2>
              <p className="text-white/70 text-lg mb-8 leading-relaxed">
                Stop guessing rehab costs. Post your scope, receive itemized bids from 
                insured contractors, and watch the estimates flow directly into your deal calculator. 
                No more deal-killing surprises at closing.
              </p>
              <ul className="space-y-4">
                {[
                  "Contractors verified with insurance and business docs",
                  "Bids auto-update financial projections in real-time",
                  "Compare bids, award jobs, track progress—all in-platform",
                  "Build relationships with reliable contractors over time"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-black border border-white/[0.08] p-8">
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-4 border-b border-white/[0.06]">
                  <span className="text-white/70 text-sm uppercase">Contractor Bid</span>
                  <span className="text-xs px-2 py-1 bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border border-emerald-500/30">VERIFIED</span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/70">Kitchen remodel</span>
                    <span className="text-white font-mono">$12,500</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">2x bathroom updates</span>
                    <span className="text-white font-mono">$8,200</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Flooring (1,200 sqft)</span>
                    <span className="text-white font-mono">$6,800</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Paint interior</span>
                    <span className="text-white font-mono">$3,500</span>
                  </div>
                </div>
                <div className="pt-4 border-t border-white/[0.06] flex justify-between">
                  <span className="text-white/70 font-bold">Total Bid</span>
                  <span className="text-gold font-mono font-bold text-xl">$31,000</span>
                </div>
                <div className="pt-2 text-xs text-white/70">
                  ↳ Auto-synced to deal calculator → Updated ROI: 24.3%
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" ref={pricingRef} className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase text-white mb-4">
              INVEST IN YOUR INFRASTRUCTURE
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              DealForge replaces multiple paid tools. The cost is the consolidation dividend—
              one system, one login, one source of truth.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, idx) => (
              <div 
                key={idx}
                className={`relative bg-[#0A0A0A]/80 backdrop-blur-sm/50 border ${plan.highlighted ? 'border-gold' : 'border-white/[0.08]'} p-8 ${plan.highlighted ? 'ring-1 ring-gold/20' : ''}`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gold text-black text-xs font-bold uppercase tracking-wider">
                    {plan.badge}
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className="font-heading text-xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-sm text-white/70">{plan.description}</p>
                </div>
                
                <div className="mb-6">
                  <span className="font-mono text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-white/70">{plan.period}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                      <span className="text-sm text-white/70">{feature}</span>
                    </li>
                  ))}
                  {plan.excluded.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 opacity-40">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-1">
                        <div className="w-1 h-1 bg-white/[0.1] rounded-full" />
                      </div>
                      <span className="text-sm text-white/70">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full rounded-none font-bold uppercase tracking-wide ${plan.highlighted ? 'gold-gradient-btn' : 'bg-white/[0.08] text-white hover:bg-white/[0.1] border border-white/[0.1]'}`}
                  onClick={() => navigate('/register')}
                  data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
          
          {/* Portal pricing callouts */}
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            <div className="flex items-center gap-3 px-4 py-3 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08]">
              <Briefcase className="w-5 h-5 text-gold" />
              <div>
                <span className="text-white font-bold text-sm">Contractors</span>
                <span className="text-white/50 text-sm ml-1">$99.99/mo</span>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08]">
              <Building2 className="w-5 h-5 text-gold" />
              <div>
                <span className="text-white font-bold text-sm">Lenders</span>
                <span className="text-white/50 text-sm ml-1">$299.99/mo</span>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08]">
              <Home className="w-5 h-5 text-gold" />
              <div>
                <span className="text-white font-bold text-sm">Realtors</span>
                <span className="text-white/50 text-sm ml-1">$99.99/mo</span>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08]">
              <Shield className="w-5 h-5 text-gold" />
              <div>
                <span className="text-white font-bold text-sm">Title</span>
                <span className="text-white/50 text-sm ml-1">$199.99/mo</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="py-24 bg-[#0A0A0A]/80 backdrop-blur-sm/30 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase text-white mb-4">
              THE DEALFORGE <span className="text-gold">ECOSYSTEM</span>
            </h2>
            <p className="text-white/70 max-w-3xl mx-auto text-lg">
              Every deal needs contractors, funding, clear title, and sometimes a realtor. DealForge connects all the players 
              in one platform—so you stop chasing and start closing.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {/* Contractor Marketplace */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08] p-8 hover:border-gold/30 transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <HardHat className="w-7 h-7 text-gold" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-white">CONTRACTOR PORTAL</h3>
                  <p className="text-gold font-bold">$99.99/mo</p>
                </div>
              </div>
              
              <p className="text-white/70 mb-6">
                Stop cold-calling contractors. Post your rehab scope, get competitive bids from verified professionals, and track every project.
              </p>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Verified insurance & licensing</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Itemized bids flow into your calculator</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Reviews & ratings from real operators</span>
                </li>
              </ul>
              
              <Link to="/contractor/register">
                <Button className="w-full rounded-none font-bold uppercase tracking-wide bg-white/[0.08] text-white hover:bg-white/[0.1] border border-white/[0.1]">
                  Join as Contractor
                </Button>
              </Link>
            </div>

            {/* Lender Network */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08] p-8 hover:border-gold/30 transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-gold" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-white">LENDER NETWORK</h3>
                  <p className="text-gold font-bold">$299.99/mo</p>
                </div>
              </div>
              
              <p className="text-white/70 mb-6">
                Hard money, private money, bridge loans. Get funding offers before you close—from lenders who see your full deal package.
              </p>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Access to pre-qualified deal flow</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Deal packets with full financials</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Submit funding offers directly</span>
                </li>
              </ul>
              
              <Link to="/lender/login">
                <Button className="w-full rounded-none font-bold uppercase tracking-wide bg-white/[0.08] text-white hover:bg-white/[0.1] border border-white/[0.1]">
                  Join as Lender
                </Button>
              </Link>
            </div>

            {/* Realtor Portal */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08] p-8 hover:border-gold/30 transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <Home className="w-7 h-7 text-gold" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-white">REALTOR PORTAL</h3>
                  <p className="text-gold font-bold">$99.99/mo</p>
                </div>
              </div>
              
              <p className="text-white/70 mb-6">
                Manage listings, track offers, coordinate transactions—and request title services from verified companies who compete for your business.
              </p>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Full transaction workflow management</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Title service marketplace integration</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Buyer/seller pipeline tracking</span>
                </li>
              </ul>
              
              <Link to="/realtor">
                <Button className="w-full rounded-none font-bold uppercase tracking-wide bg-white/[0.08] text-white hover:bg-white/[0.1] border border-white/[0.1]">
                  Join as Realtor
                </Button>
              </Link>
            </div>

            {/* Title Portal */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08] p-8 hover:border-gold/30 transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <Shield className="w-7 h-7 text-gold" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-white">TITLE PORTAL</h3>
                  <p className="text-gold font-bold">$199.99/mo</p>
                </div>
              </div>
              
              <p className="text-white/70 mb-6">
                Bid on title service requests from realtors and investors. Win more closings by competing on price, speed, and service.
              </p>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Access to title service marketplace</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Full order management workflow</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/70">Build pipeline from won bids</span>
                </li>
              </ul>
              
              <Link to="/title">
                <Button className="w-full rounded-none font-bold uppercase tracking-wide bg-white/[0.08] text-white hover:bg-white/[0.1] border border-white/[0.1]">
                  Join as Title Company
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#0A0A0A]/80 backdrop-blur-sm/30 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src="/logo.png" alt="DealForge" className="h-32 w-auto mx-auto mb-10" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase text-white mb-6">
            BUILD YOUR OPERATION ON <span className="text-gold">SOLID GROUND</span>
          </h2>
          <p className="text-white/70 mb-10 text-lg">
            DealForge is infrastructure, not software you casually try. If you're serious about 
            building a real estate operation that scales, this is the foundation.
          </p>
          <Link to="/register">
            <Button className="gold-gradient-btn h-14 px-12 text-base rounded-none" data-testid="final-cta-btn">
              REQUEST EARLY ACCESS
            </Button>
          </Link>
          <p className="text-xs text-white/70 mt-6">
            Founders pricing ends when we hit capacity. No extensions.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center">
              <img src="/logo.png" alt="DealForge" className="h-16 w-auto" />
            </div>
            
            <div className="flex items-center gap-8 text-sm text-white/70">
              <Link to="/contractor/register" className="hover:text-white/70 transition-all duration-300">For Contractors</Link>
              <Link to="/lender/login" className="hover:text-white/70 transition-all duration-300">For Lenders</Link>
              <span>© 2026 DealForge. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
